import React, { useState, useEffect, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { PageTitle } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Share2, ExternalLink, Users2, Check, X, ImagePlus, Eye, Plus, Trash2, CalendarDays, Save } from "lucide-react";
import { TEMPLATES, TEMPLATE_CATS } from "@/lib/templates";
import InvitationPreview from "@/components/InvitationPreview";
import { BACKGROUNDS } from "@/lib/backgrounds";
import { defaultDesign, mergeDesign, emptyStory, emptyAccount, emptyEvent } from "@/lib/design";
import { PREMIUM_THEMES, isStandardTheme } from "@/lib/premiumThemes";
import { TF, TA, SW, PhotoField } from "@/components/InvFields";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import { toast } from "sonner";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";

const PHOTO_LAYOUTS = [
  { value: "background", label: "Latar Belakang (penuh)" },
  { value: "top", label: "Atas (persegi)" },
  { value: "bottom", label: "Bawah (persegi)" },
  { value: "circle", label: "Bulat di Atas" },
];

const TABS = [
  ["detail", "Detail Inti"], ["cover", "Cover"], ["pembuka", "Pembuka"],
  ["pria", "Mempelai Pria"], ["wanita", "Mempelai Wanita"], ["countdown", "Count Down"],
  ["acara", "Acara"], ["kisah", "Kisah Kami"], ["galeri", "Galeri"],
  ["gift", "Wedding Gift"], ["rsvp", "RSVP"], ["ucapan", "Ucapan"], ["penutup", "Penutup"],
];

const Person = ({ sec, design, setD, uploading, uploadDesign }) => {
  const p = design[sec];
  return (
    <div className="space-y-4">
      <TF label="Nama Lengkap" value={p.nama_lengkap} onChange={(v) => setD(sec, "nama_lengkap", v)} testid={`${sec}-nama`} />
      <TA label="Bio Singkat" value={p.bio} onChange={(v) => setD(sec, "bio", v)} testid={`${sec}-bio`} placeholder="Putra/Putri dari ..." />
      <div className="grid gap-4 sm:grid-cols-2">
        <TF label="Nama Ayah" value={p.ayah} onChange={(v) => setD(sec, "ayah", v)} testid={`${sec}-ayah`} />
        <TF label="Nama Ibu" value={p.ibu} onChange={(v) => setD(sec, "ibu", v)} testid={`${sec}-ibu`} />
      </div>
      <TF label="Instagram (username tanpa @)" value={p.instagram} onChange={(v) => setD(sec, "instagram", v)} testid={`${sec}-ig`} placeholder="mis. budi.santoso" />
      <PhotoField label="Foto" uploaded={p.foto} url={p.foto_url} uploading={uploading === sec}
        onUpload={uploadDesign(sec, "foto", sec)} onUrl={(v) => setD(sec, "foto_url", v)} onClear={() => setD(sec, "foto", "")} testid={`${sec}-foto`} />
    </div>
  );
};

const SortableStoryItem = ({ st, setStory, removeStory }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: st._id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} data-testid={`story-block-${st._id}`} className="space-y-3 rounded-xl border border-border bg-background p-4">
      <div className="flex items-center justify-between">
        <button data-testid={`story-drag-${st._id}`} {...attributes} {...listeners} className="cursor-grab text-muted-foreground hover:text-foreground active:cursor-grabbing" title="Seret untuk mengubah urutan">
          <GripVertical className="h-5 w-5" />
        </button>
        <Button data-testid={`remove-story-${st._id}`} onClick={() => removeStory(st._id)} size="icon" variant="ghost" className="h-7 w-7 text-red-500"><Trash2 className="h-4 w-4" /></Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <TF label="Tahun" value={st.tahun} onChange={(v) => setStory(st._id, "tahun", v)} testid={`story-tahun-${st._id}`} placeholder="mis. 2021" />
        <TF label="Judul" value={st.judul} onChange={(v) => setStory(st._id, "judul", v)} testid={`story-judul-${st._id}`} />
      </div>
      <TA label="Cerita Singkat" value={st.cerita} onChange={(v) => setStory(st._id, "cerita", v)} testid={`story-cerita-${st._id}`} />
    </div>
  );
};

function resizeImage(file, max = 1000) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > max) { height = (height * max) / width; width = max; }
        else if (height > max) { width = (width * max) / height; height = max; }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Undangan() {
  const { user } = useAuth();
  const { data, activePasangan, pasanganInfo } = useData();
  const canSelect = user?.role === "sysadmin" || user?.role === "Admin";
  const pid = canSelect ? activePasangan : user?.pasangan_id;
  const params = canSelect && pid ? { pasangan_id: pid } : {};
  const { canAccess, getQuota, planLabel } = useFeatureAccess();
  const canStandardTpl = canAccess("template_standard");
  const canPremiumTpl = canAccess("template_premium");
  const galleryLimit = getQuota("max_gallery_photos"); // null = unlimited

  const [s, setS] = useState({ greeting: "", couple_photo: "", template: "1", music_url: "", music_start_seconds: 0, photo_layout: "background", bg_theme: "none", wedding_date: "", wedding_time: "09:00", premium_template: "editorial-bw", active_kind: "premium" });
  const [events, setEvents] = useState([emptyEvent("Akad Nikah")]);
  const [gallery, setGallery] = useState([]);
  const [design, setDesign] = useState(defaultDesign());
  const [weddingDate, setWeddingDate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const [galUploading, setGalUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const galRef = useRef(null);

  useEffect(() => {
    if (canSelect && !pid) return;
    api.get("/settings", { params }).then((r) => {
      const d = r.data || {};
      setS({
        greeting: d.greeting || "", couple_photo: d.couple_photo || "",
        template: d.template || "1", music_url: d.music_url || "",
        music_start_seconds: d.music_start_seconds || 0,
        photo_layout: d.photo_layout || "background", bg_theme: d.bg_theme || "none",
        wedding_date: d.wedding_date || "", wedding_time: d.wedding_time || "",
        premium_template: d.premium_template || "editorial-bw",
        active_kind: d.active_kind || (d.premium_template ? "premium" : "standard"),
      });
      const stored = Array.isArray(d.events) ? d.events.filter((e) => e && typeof e === "object") : [];
      if (stored.length > 0) setEvents(stored.slice(0, 2).map((e) => ({ ...emptyEvent(), ...e })));
      else setEvents([{ ...emptyEvent("Akad Nikah"), tanggal: d.wedding_date || "", jam: d.wedding_time || "", tempat: d.venue_name || "", alamat: d.venue_address || "", maps_url: d.maps_url || "" }]);
      setGallery(Array.isArray(d.gallery) ? d.gallery : []);
      const dz = mergeDesign(d.design);
      if (!d.design && d.nama) dz.cover.eyebrow_bottom = d.nama;
      setDesign(dz);
      setWeddingDate(d.wedding_date || null);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid, canSelect]);

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setD = (section, key, v) => setDesign((p) => ({ ...p, [section]: { ...p[section], [key]: v } }));
  const setEvent = (i, k, v) => setEvents((prev) => prev.map((e, idx) => (idx === i ? { ...e, [k]: v } : e)));
  const addEvent = () => setEvents((prev) => (prev.length >= 2 ? prev : [...prev, emptyEvent("Resepsi")]));
  const removeEvent = (i) => setEvents((prev) => prev.filter((_, idx) => idx !== i));

  // Kisah Kami
  const setStory = (id, k, v) => setDesign((p) => ({ ...p, kisah: { ...p.kisah, items: p.kisah.items.map((it) => (it._id === id ? { ...it, [k]: v } : it)) } }));
  const addStory = () => setDesign((p) => ({ ...p, kisah: { ...p.kisah, items: [...p.kisah.items, emptyStory()] } }));
  const removeStory = (id) => setDesign((p) => ({ ...p, kisah: { ...p.kisah, items: p.kisah.items.filter((it) => it._id !== id) } }));
  const reorderStory = (fromId, toId) => setDesign((p) => {
    const items = p.kisah.items;
    const fromIdx = items.findIndex((it) => it._id === fromId);
    const toIdx = items.findIndex((it) => it._id === toId);
    if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return p;
    return { ...p, kisah: { ...p.kisah, items: arrayMove(items, fromIdx, toIdx) } };
  });
  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  // Wedding Gift accounts
  const setAcc = (i, k, v) => setDesign((p) => ({ ...p, gift: { ...p.gift, accounts: p.gift.accounts.map((a, idx) => (idx === i ? { ...a, [k]: v } : a)) } }));
  const addAcc = () => setDesign((p) => ({ ...p, gift: { ...p.gift, accounts: [...p.gift.accounts, emptyAccount()] } }));
  const removeAcc = (i) => setDesign((p) => ({ ...p, gift: { ...p.gift, accounts: p.gift.accounts.filter((_, idx) => idx !== i) } }));

  const uploadDesign = (section, key, tag) => async (file) => {
    if (!file.type.startsWith("image/")) { toast.error("Pilih file gambar"); return; }
    setUploading(tag);
    try { const b64 = await resizeImage(file); setD(section, key, b64); toast.success("Foto diunggah (klik Simpan untuk menyimpan)"); }
    catch { toast.error("Gagal mengunggah"); }
    finally { setUploading(""); }
  };

  const onCouplePhoto = async (file) => {
    setUploading("couple");
    try { const b64 = await resizeImage(file); set("couple_photo", b64); toast.success("Foto pasangan diunggah"); }
    catch { toast.error("Gagal mengunggah"); }
    finally { setUploading(""); }
  };

  const onGallery = async (e) => {
    const files = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
    if (files.length === 0) return;
    const cap = galleryLimit === null ? Infinity : galleryLimit;
    const room = cap - gallery.length;
    if (room <= 0) {
      toast.error(`Batas foto galeri (${galleryLimit}) untuk ${planLabel} sudah tercapai. Upgrade paket untuk menambah lebih banyak.`);
      return;
    }
    setGalUploading(true);
    try {
      const encoded = await Promise.all(files.slice(0, room).map((f) => resizeImage(f, 1200)));
      const next = [...gallery, ...encoded];
      setGallery(next);
      await api.put("/settings", { gallery: next }, { params });
      toast.success(`${encoded.length} foto ditambahkan`);
    } catch { toast.error("Gagal mengunggah foto"); }
    finally { setGalUploading(false); if (galRef.current) galRef.current.value = ""; }
  };

  const removeGalPhoto = async (idx) => {
    const next = gallery.filter((_, i) => i !== idx);
    setGallery(next);
    try { await api.put("/settings", { gallery: next }, { params }); toast.success("Foto dihapus"); }
    catch { toast.error("Gagal menghapus foto"); }
  };

  const pickTemplate = (id) => set("template", id);

  const save = async () => {
    setSaving(true);
    try {
      const ev0 = events[0] || {};
      await api.put("/settings", {
        greeting: s.greeting, music_url: s.music_url,
        music_start_seconds: parseInt(s.music_start_seconds, 10) || 0,
        template: s.template,
        couple_photo: s.couple_photo, photo_layout: s.photo_layout, bg_theme: s.bg_theme,
        premium_template: s.premium_template, active_kind: s.active_kind,
        wedding_date: s.wedding_date || "", wedding_time: s.wedding_time || "",
        events, gallery, design,
        venue_name: ev0.tempat || "", venue_address: ev0.alamat || "", maps_url: ev0.maps_url || "",
      }, { params });
      setWeddingDate(s.wedding_date || null);
      toast.success("Kustomisasi undangan disimpan");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const link = pid ? `${window.location.origin}/u/${pid}` : "";
  const share = async () => {
    if (!link) { toast.error("Pilih pasangan dulu"); return; }
    try { await navigator.clipboard.writeText(link); toast.success("Link undangan disalin!"); }
    catch { toast.info(link); }
  };

  const rsvps = [...(data.rsvp || [])].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const hadir = rsvps.filter((r) => r.kehadiran === "Hadir");
  const totalOrang = hadir.reduce((a, r) => a + (parseInt(r.jumlah, 10) || 1), 0);

  return (
    <div>
      <PageTitle title="Kustomisasi Undangan" subtitle="Rancang undangan digital Anda per bagian" />

      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start lg:gap-8">
        <div className="min-w-0">
          {/* Link + actions */}
          <Card className="mb-6 flex flex-wrap items-center justify-between gap-4 p-5">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Link Undangan Publik</p>
              <p className="mt-1 break-all font-mono text-sm">{link || "(pilih pasangan di Home)"}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button data-testid="save-invitation" onClick={save} disabled={saving} className="gap-2 rounded-full"><Save className="h-4 w-4" />{saving ? "Menyimpan..." : "Simpan"}</Button>
              <Button data-testid="share-invite-btn" onClick={share} variant="outline" className="gap-2 rounded-full"><Share2 className="h-4 w-4" /> Salin Link</Button>
              <Button data-testid="preview-invite-btn" onClick={() => setPreviewOpen(true)} variant="outline" className="gap-2 rounded-full"><Eye className="h-4 w-4" /> Preview</Button>
              {link && (
                <a href={link} target="_blank" rel="noreferrer" data-testid="open-invite-link">
                  <Button className="gap-2 rounded-full" style={{ background: "linear-gradient(90deg,#c9a35c,#e6c98a)", color: "#0f0a08" }}>
                    <ExternalLink className="h-4 w-4" /> Buka
                  </Button>
                </a>
              )}
            </div>
          </Card>

          <Card className="p-4 sm:p-6">
            <Tabs defaultValue="detail" className="w-full">
              <div className="-mx-1 mb-5 overflow-x-auto pb-1">
                <TabsList className="inline-flex h-auto w-max flex-nowrap gap-1 bg-muted/50 p-1">
                  {TABS.map(([v, l]) => (
                    <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="whitespace-nowrap text-xs">{l}</TabsTrigger>
                  ))}
                </TabsList>
              </div>

              {/* DETAIL INTI */}
              <TabsContent value="detail" className="space-y-5">
                <TF label="Nama Singkatan Pasangan" value={design.detail.nama_singkat} onChange={(v) => setD("detail", "nama_singkat", v)} placeholder="mis. Budi & Ani" testid="detail-nama-singkat" />
                <TF label="Lokasi Singkat" value={design.detail.lokasi_singkat} onChange={(v) => setD("detail", "lokasi_singkat", v)} placeholder="mis. Jakarta" hint="Info lokasi singkat di undangan." testid="detail-lokasi" />
                <div className="grid gap-4 sm:grid-cols-2">
                  <TF label="Tanggal Hari H" type="date" value={s.wedding_date} onChange={(v) => set("wedding_date", v)} testid="detail-tanggal" hint="Dipakai untuk countdown." />
                  <TF label="Waktu Hari H" type="time" value={s.wedding_time} onChange={(v) => set("wedding_time", v)} testid="detail-waktu" />
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Template Undangan Aktif — {s.active_kind === "premium"
                      ? (PREMIUM_THEMES.find((t) => t.id === s.premium_template)?.name || "Editorial Monokrom")
                      : `Standar #${s.template}`}
                  </Label>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Anda hanya bisa memilih <b>satu</b> template aktif. Memilih template baru otomatis mengganti yang sebelumnya.
                    Link unik yang dibagikan ke tamu:{" "}
                    <code className="rounded bg-muted px-1">{link || "(pilih pasangan)"}</code>
                    <span className="ml-2 opacity-70">— siapapun yang membukanya akan otomatis melihat template terpilih.</span>
                  </p>

                  {/* PREMIUM (mobile-centered) — 7 templates */}
                  <p className="mt-4 mb-2 text-[11px] font-semibold uppercase tracking-wider text-primary">Premium (Mobile Elegan)</p>
                  <div className="grid grid-cols-2 gap-2 rounded-xl border border-border p-3 sm:grid-cols-3 md:grid-cols-4" data-testid="premium-template-gallery">
                    {PREMIUM_THEMES.map((t) => {
                      const active = s.active_kind === "premium" && s.premium_template === t.id;
                      const locked = !canPremiumTpl;
                      const pickTheme = () => {
                        if (locked) {
                          toast.info(`Template Premium tidak tersedia di ${planLabel}. Upgrade paket untuk mengaksesnya.`);
                          return;
                        }
                        setS((p) => ({ ...p, premium_template: t.id, active_kind: "premium" }));
                      };
                      return (
                        <button key={t.id} type="button" data-testid={`premium-template-${t.id}`} onClick={pickTheme}
                          className={`relative overflow-hidden rounded-xl border text-left transition-transform hover:-translate-y-0.5 ${active ? "ring-2 ring-primary" : "border-border"} ${locked ? "opacity-50" : ""}`}>
                          <div className="relative h-24 w-full" style={{ background: t.palette.bg }}>
                            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap text-2xl italic" style={{ fontFamily: t.fonts.script, color: t.palette.cream }}>Aa &amp; Bb</span>
                            <span className="absolute bottom-1.5 left-1/2 h-0.5 w-8 -translate-x-1/2 rounded-full" style={{ background: t.palette.accent }} />
                            <span className="absolute right-1.5 top-1.5 h-3 w-3 rounded-full" style={{ background: t.palette.accent }} />
                            {locked && <span className="absolute inset-0 flex items-center justify-center bg-black/40 text-[9px] font-semibold uppercase tracking-[0.2em] text-amber-300">Premium</span>}
                            {active && <span className="absolute inset-0 border-4 border-primary/70" />}
                          </div>
                          <div className="border-t border-border bg-background/60 px-2 py-1.5">
                            <p className="truncate text-[11px] font-semibold">{t.name}</p>
                            <p className="truncate text-[9px] text-muted-foreground">{t.cat}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* STANDARD (desktop-wide) */}
                  <p className="mt-5 mb-2 text-[11px] font-semibold uppercase tracking-wider text-primary">Standar (Desktop Wide)</p>
                  <div className="max-h-72 space-y-4 overflow-y-auto rounded-xl border border-border p-3">
                    {TEMPLATE_CATS.map((cat) => (
                      <div key={cat}>
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{cat}</p>
                        <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                          {TEMPLATES.filter((t) => t.cat === cat).map((t) => {
                            const active = s.active_kind === "standard" && s.template === t.id;
                            const stdLocked = !canStandardTpl;
                            const pickStd = () => {
                              if (stdLocked) {
                                toast.info(`Template Standar tidak tersedia di ${planLabel}. Upgrade paket untuk mengaksesnya.`);
                                return;
                              }
                              setS((p) => ({ ...p, template: t.id, active_kind: "standard" }));
                            };
                            return (
                              <button key={t.id} data-testid={`template-${t.id}`}
                                onClick={pickStd}
                                className={`overflow-hidden rounded-lg border transition-transform hover:-translate-y-0.5 ${active ? "ring-2 ring-primary" : "border-border"} ${stdLocked ? "opacity-50" : ""}`}>
                                <div className="flex h-16 flex-col items-center justify-center gap-1" style={{ background: t.bg || t.base }}>
                                  <span style={{ fontFamily: t.font, color: t.accent }} className="text-sm">Aa</span>
                                  <span className="h-1 w-6 rounded-full" style={{ background: t.accent }} />
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Latar Belakang Bertema</Label>
                  <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-6" data-testid="bg-theme-gallery">
                    {BACKGROUNDS.map((b) => (
                      <button key={b.id} type="button" data-testid={`bg-theme-${b.id}`} onClick={() => set("bg_theme", b.id)}
                        className={`relative aspect-video overflow-hidden rounded-lg border-2 ${s.bg_theme === b.id ? "border-primary ring-2 ring-primary/40" : "border-border"}`} title={b.label}>
                        {b.url ? <img src={b.url} alt={b.label} loading="lazy" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center bg-muted text-[10px] text-muted-foreground">Tanpa</div>}
                        <span className="absolute inset-x-0 bottom-0 bg-black/50 px-1 py-0.5 text-[8px] text-white">{b.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <PhotoField label="Atau Upload Foto Latar Sendiri" hint="Saran: foto lanskap resolusi tinggi. Diprioritaskan sebagai latar bila layout 'Latar Belakang'." uploaded={s.couple_photo} url=""
                  uploading={uploading === "couple"} onUpload={onCouplePhoto} onUrl={() => {}} onClear={() => set("couple_photo", "")} testid="couple-photo" />

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Penempatan Foto</Label>
                    <Select value={s.photo_layout} onValueChange={(v) => set("photo_layout", v)}>
                      <SelectTrigger data-testid="inv-photo-layout" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>{PHOTO_LAYOUTS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Ukuran Foto Latar</Label>
                    <Select value={design.bg.fit} onValueChange={(v) => setD("bg", "fit", v)}>
                      <SelectTrigger data-testid="bg-fit" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="cover">Penuhi layar (cover)</SelectItem><SelectItem value="contain">Muat penuh (contain)</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Keburaman Foto Latar: {design.bg.opacity}%</Label>
                  <input type="range" min="10" max="90" value={design.bg.opacity} data-testid="bg-opacity" onChange={(e) => setD("bg", "opacity", parseInt(e.target.value, 10))} className="mt-2 w-full accent-primary" />
                </div>
              </TabsContent>

              {/* COVER */}
              <TabsContent value="cover" className="space-y-5">
                <SW label="Gunakan Halaman Cover" checked={design.cover.enabled} onChange={(v) => setD("cover", "enabled", v)} testid="cover-enabled" />
                <div className="grid gap-4 sm:grid-cols-2">
                  <TF label="Eyebrow Atas" value={design.cover.eyebrow_top} onChange={(v) => setD("cover", "eyebrow_top", v)} placeholder="The Wedding Of" testid="cover-eyebrow-top" />
                  <TF label="Eyebrow Bawah" value={design.cover.eyebrow_bottom} onChange={(v) => setD("cover", "eyebrow_bottom", v)} hint="Biasanya tanggal" testid="cover-eyebrow-bottom" />
                  <TF label="Nama Pria" value={design.cover.nama_pria} onChange={(v) => setD("cover", "nama_pria", v)} testid="cover-nama-pria" />
                  <TF label="Nama Wanita" value={design.cover.nama_wanita} onChange={(v) => setD("cover", "nama_wanita", v)} testid="cover-nama-wanita" />
                </div>
                <TF label="Subline / Deskripsi" value={design.cover.subline} onChange={(v) => setD("cover", "subline", v)} testid="cover-subline" />
                <div className="grid gap-4 sm:grid-cols-2">
                  <TF label="Default Kepada" value={design.cover.default_to} onChange={(v) => setD("cover", "default_to", v)} placeholder="Tamu Undangan" testid="cover-default-to" />
                  <TF label="Label Tombol Buka" value={design.cover.button_label} onChange={(v) => setD("cover", "button_label", v)} placeholder="Buka Undangan" testid="cover-button-label" />
                </div>
                <PhotoField label="Foto Cover" uploaded={design.cover.photo} url={design.cover.photo_url} uploading={uploading === "cover"}
                  onUpload={uploadDesign("cover", "photo", "cover")} onUrl={(v) => setD("cover", "photo_url", v)} onClear={() => setD("cover", "photo", "")} testid="cover-photo" />

                {/* Musik Latar — semua paket */}
                <div className="rounded-xl border border-border bg-muted/30 p-4">
                  <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Musik Latar <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[9px] text-primary">Semua Paket</span>
                  </p>
                  <div className="grid gap-3 sm:grid-cols-[1fr_120px]">
                    <TF label="Link Lagu" value={s.music_url} onChange={(v) => set("music_url", v)} testid="music-url"
                      placeholder="YouTube / Spotify / direct .mp3 URL" />
                    <TF label="Mulai dari (detik)" type="number" value={s.music_start_seconds}
                      onChange={(v) => set("music_start_seconds", parseInt(v, 10) || 0)} testid="music-start" placeholder="0" />
                  </div>
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    Lagu diputar otomatis (loop) saat tamu membuka undangan. Tombol play/pause tampil di sudut halaman.
                  </p>
                </div>
              </TabsContent>

              {/* PEMBUKA */}
              <TabsContent value="pembuka" className="space-y-5">
                <SW label="Gunakan Bagian Pembuka" checked={design.pembuka.enabled} onChange={(v) => setD("pembuka", "enabled", v)} testid="pembuka-enabled" />
                <TF label="Pembuka (opsional)" value={design.pembuka.text} onChange={(v) => setD("pembuka", "text", v)} placeholder="Bismillahir rahmanir rohim" testid="pembuka-text" />
                <TA label="Quotes / Ayat" value={design.pembuka.quote} onChange={(v) => setD("pembuka", "quote", v)} testid="pembuka-quote" />
                <TF label="Sumber Sitasi / Ayat" value={design.pembuka.quote_source} onChange={(v) => setD("pembuka", "quote_source", v)} placeholder="QS. Ar-Rum: 21" testid="pembuka-source" />
                <TA label="Intro Mempelai" value={design.pembuka.intro} onChange={(v) => setD("pembuka", "intro", v)} testid="pembuka-intro" />
                <PhotoField label="Latar Belakang Pembuka" uploaded={design.pembuka.bg} url={design.pembuka.bg_url} uploading={uploading === "pembuka"}
                  onUpload={uploadDesign("pembuka", "bg", "pembuka")} onUrl={(v) => setD("pembuka", "bg_url", v)} onClear={() => setD("pembuka", "bg", "")} testid="pembuka-bg" />
              </TabsContent>

              <TabsContent value="pria"><Person sec="pria" design={design} setD={setD} uploading={uploading} uploadDesign={uploadDesign} /></TabsContent>
              <TabsContent value="wanita"><Person sec="wanita" design={design} setD={setD} uploading={uploading} uploadDesign={uploadDesign} /></TabsContent>

              {/* COUNTDOWN */}
              <TabsContent value="countdown" className="space-y-5">
                <SW label="Gunakan Countdown" checked={design.countdown.enabled} onChange={(v) => setD("countdown", "enabled", v)} testid="countdown-enabled" />
                <div className="grid gap-4 sm:grid-cols-2">
                  <TF label="Tanggal Hari H" type="date" value={s.wedding_date} onChange={(v) => set("wedding_date", v)} testid="countdown-tanggal" />
                  <TF label="Waktu Hari H" type="time" value={s.wedding_time} onChange={(v) => set("wedding_time", v)} testid="countdown-waktu" />
                </div>
                <TF label="Label Atas" value={design.countdown.label_atas} onChange={(v) => setD("countdown", "label_atas", v)} testid="countdown-label" />
                <TF label="Label Tombol Kalender" value={design.countdown.calendar_label} onChange={(v) => setD("countdown", "calendar_label", v)} hint="Tamu bisa menambahkan jadwal ke kalender mereka." testid="countdown-calendar" />
                <PhotoField label="Foto Section Countdown" uploaded={design.countdown.photo} url={design.countdown.photo_url} uploading={uploading === "countdown"}
                  onUpload={uploadDesign("countdown", "photo", "countdown")} onUrl={(v) => setD("countdown", "photo_url", v)} onClear={() => setD("countdown", "photo", "")} testid="countdown-photo" />
              </TabsContent>

              {/* ACARA */}
              <TabsContent value="acara" className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold">Acara ({events.length}/2)</p>
                  {events.length < 2 && <Button data-testid="add-event-btn" onClick={addEvent} size="sm" variant="outline" className="gap-1 rounded-full"><Plus className="h-4 w-4" /> Tambah Acara</Button>}
                </div>
                {events.map((ev, i) => (
                  <div key={i} data-testid={`event-block-${i}`} className="space-y-3 rounded-xl border border-border p-4">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><CalendarDays className="h-4 w-4" /> Acara {i + 1}</span>
                      {events.length > 1 && <Button data-testid={`remove-event-${i}`} onClick={() => removeEvent(i)} size="icon" variant="ghost" className="h-7 w-7 text-red-500"><Trash2 className="h-4 w-4" /></Button>}
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <TF label="Nama Acara" value={ev.nama} onChange={(v) => setEvent(i, "nama", v)} testid={`event-nama-${i}`} />
                      <TF label="Tempat" value={ev.tempat} onChange={(v) => setEvent(i, "tempat", v)} testid={`event-tempat-${i}`} />
                      <TF label="Tanggal" type="date" value={ev.tanggal} onChange={(v) => setEvent(i, "tanggal", v)} testid={`event-tanggal-${i}`} />
                      <TF label="Waktu" type="time" value={ev.jam} onChange={(v) => setEvent(i, "jam", v)} testid={`event-jam-${i}`} />
                      <TF label="Dress Code" value={ev.dresscode} onChange={(v) => setEvent(i, "dresscode", v)} testid={`event-dresscode-${i}`} placeholder="mis. Batik / Formal" />
                      <TF label="URL Google Maps" value={ev.maps_url} onChange={(v) => setEvent(i, "maps_url", v)} testid={`event-maps-${i}`} />
                    </div>
                    <TA label="Alamat" value={ev.alamat} onChange={(v) => setEvent(i, "alamat", v)} testid={`event-alamat-${i}`} />
                  </div>
                ))}
                <PhotoField label="Foto Section Waktu &amp; Tempat" uploaded={design.acara.photo} url={design.acara.photo_url} uploading={uploading === "acara"}
                  onUpload={uploadDesign("acara", "photo", "acara")} onUrl={(v) => setD("acara", "photo_url", v)} onClear={() => setD("acara", "photo", "")} testid="acara-photo" />
              </TabsContent>

              {/* KISAH KAMI */}
              <TabsContent value="kisah" className="space-y-4">
                <SW label="Gunakan Kisah Kami" checked={design.kisah.enabled} onChange={(v) => setD("kisah", "enabled", v)} testid="kisah-enabled" />
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold">Daftar Kisah ({design.kisah.items.length}) <span className="ml-2 text-xs font-normal text-muted-foreground">seret ikon ⋮⋮ untuk atur urutan</span></p>
                  <Button data-testid="add-story-btn" onClick={addStory} size="sm" variant="outline" className="gap-1 rounded-full"><Plus className="h-4 w-4" /> Tambah Kisah</Button>
                </div>
                <DndContext sensors={dndSensors} collisionDetection={closestCenter}
                  onDragEnd={(e) => { const { active, over } = e; if (over && active.id !== over.id) reorderStory(active.id, over.id); }}>
                  <SortableContext items={design.kisah.items.map((it) => it._id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-3">
                      {design.kisah.items.map((st) => (
                        <SortableStoryItem key={st._id} st={st} setStory={setStory} removeStory={removeStory} />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
                <PhotoField label="Foto Section Kisah Cinta" uploaded={design.kisah.photo} url={design.kisah.photo_url} uploading={uploading === "kisah"}
                  onUpload={uploadDesign("kisah", "photo", "kisah")} onUrl={(v) => setD("kisah", "photo_url", v)} onClear={() => setD("kisah", "photo", "")} testid="kisah-photo" />
              </TabsContent>

              {/* GALERI */}
              <TabsContent value="galeri" className="space-y-4">
                <SW label="Gunakan Galeri" checked={design.galeri.enabled} onChange={(v) => setD("galeri", "enabled", v)} testid="galeri-enabled" />
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Foto Galeri ({gallery.length}{galleryLimit !== null ? `/${galleryLimit}` : ""})
                  </Label>
                  <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4">
                    {gallery.map((g, i) => (
                      <div key={i} className="group relative aspect-square overflow-hidden rounded-lg border border-border">
                        <img src={g} alt={`g${i}`} className="h-full w-full object-cover" />
                        <button data-testid={`gallery-remove-${i}`} onClick={() => removeGalPhoto(i)} className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"><X className="h-3 w-3" /></button>
                      </div>
                    ))}
                    {(galleryLimit === null || gallery.length < galleryLimit) && (
                      <button data-testid="gallery-add" onClick={() => galRef.current?.click()} disabled={galUploading} className="flex aspect-square items-center justify-center rounded-lg border-2 border-dashed border-border text-muted-foreground hover:border-primary">
                        <ImagePlus className="h-6 w-6" />
                      </button>
                    )}
                  </div>
                  <input ref={galRef} type="file" accept="image/*" multiple className="hidden" onChange={onGallery} data-testid="gallery-input" />
                </div>
                <TF label="Kutipan" value={design.galeri.quote} onChange={(v) => setD("galeri", "quote", v)} testid="galeri-quote" />
                <TF label="Sumber Kutipan" value={design.galeri.quote_source} onChange={(v) => setD("galeri", "quote_source", v)} testid="galeri-quote-source" />
              </TabsContent>

              {/* WEDDING GIFT */}
              <TabsContent value="gift" className="space-y-4">
                <SW label="Gunakan Wedding Gift" checked={design.gift.enabled} onChange={(v) => setD("gift", "enabled", v)} testid="gift-enabled" />
                <TA label="Intro Singkat" value={design.gift.intro} onChange={(v) => setD("gift", "intro", v)} testid="gift-intro" />
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold">Rekening / E-Wallet ({design.gift.accounts.length})</p>
                  <Button data-testid="add-account-btn" onClick={addAcc} size="sm" variant="outline" className="gap-1 rounded-full"><Plus className="h-4 w-4" /> Tambah</Button>
                </div>
                {design.gift.accounts.map((a, i) => (
                  <div key={i} data-testid={`account-block-${i}`} className="grid gap-3 rounded-xl border border-border p-4 sm:grid-cols-[1fr_1fr_1fr_auto]">
                    <TF label="Bank / E-Wallet" value={a.label} onChange={(v) => setAcc(i, "label", v)} testid={`account-label-${i}`} placeholder="BCA / GoPay" />
                    <TF label="Nomor" value={a.nomor} onChange={(v) => setAcc(i, "nomor", v)} testid={`account-nomor-${i}`} />
                    <TF label="Atas Nama" value={a.atas_nama} onChange={(v) => setAcc(i, "atas_nama", v)} testid={`account-nama-${i}`} />
                    <div className="flex items-end"><Button data-testid={`remove-account-${i}`} onClick={() => removeAcc(i)} size="icon" variant="ghost" className="text-red-500"><Trash2 className="h-4 w-4" /></Button></div>
                  </div>
                ))}
                <PhotoField label="Foto Section Wedding Gift" uploaded={design.gift.photo} url={design.gift.photo_url} uploading={uploading === "gift"}
                  onUpload={uploadDesign("gift", "photo", "gift")} onUrl={(v) => setD("gift", "photo_url", v)} onClear={() => setD("gift", "photo", "")} testid="gift-photo" />
              </TabsContent>

              {/* RSVP */}
              <TabsContent value="rsvp" className="space-y-4">
                <SW label="Gunakan RSVP" checked={design.rsvp.enabled} onChange={(v) => setD("rsvp", "enabled", v)} testid="rsvp-enabled" />
                <div className="grid gap-4 sm:grid-cols-2">
                  <TF label="Label Tombol CTA" value={design.rsvp.cta_label} onChange={(v) => setD("rsvp", "cta_label", v)} testid="rsvp-cta" />
                  <TF label="Maks Tamu per Undangan" type="number" value={design.rsvp.max_tamu} onChange={(v) => setD("rsvp", "max_tamu", parseInt(v, 10) || 1)} testid="rsvp-max" />
                </div>
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Field yang ditampilkan</p>
                  <SW label="Field Nama" checked={design.rsvp.field_nama} onChange={(v) => setD("rsvp", "field_nama", v)} testid="rsvp-field-nama" />
                  <SW label="Field Hadir / Berhalangan" checked={design.rsvp.field_kehadiran} onChange={(v) => setD("rsvp", "field_kehadiran", v)} testid="rsvp-field-kehadiran" />
                  <SW label="Field Pilih Acara (jika 2 acara)" checked={design.rsvp.field_acara} onChange={(v) => setD("rsvp", "field_acara", v)} testid="rsvp-field-acara" />
                  <SW label="Field Pesan" checked={design.rsvp.field_pesan} onChange={(v) => setD("rsvp", "field_pesan", v)} testid="rsvp-field-pesan" />
                </div>
                <Card className="p-4">
                  <div className="mb-3 flex items-center gap-4">
                    <Badge className="gap-1"><Users2 className="h-3 w-3" /> {hadir.length} RSVP Hadir</Badge>
                    <Badge variant="outline">{totalOrang} orang</Badge>
                  </div>
                  <div className="max-h-56 space-y-2 overflow-y-auto">
                    {rsvps.length === 0 ? <p className="text-sm text-muted-foreground">Belum ada konfirmasi.</p> : rsvps.map((r) => (
                      <div key={r.id} className="flex items-center justify-between rounded-lg border border-border p-2 text-sm">
                        <span>{r.nama} {r.kehadiran === "Hadir" ? <Check className="inline h-3 w-3 text-green-500" /> : <X className="inline h-3 w-3 text-red-500" />}</span>
                        <span className="text-xs text-muted-foreground">{r.jumlah || 1} org</span>
                      </div>
                    ))}
                  </div>
                </Card>
                <PhotoField label="Foto Section RSVP" uploaded={design.rsvp.photo} url={design.rsvp.photo_url} uploading={uploading === "rsvp"}
                  onUpload={uploadDesign("rsvp", "photo", "rsvp")} onUrl={(v) => setD("rsvp", "photo_url", v)} onClear={() => setD("rsvp", "photo", "")} testid="rsvp-photo" />
              </TabsContent>

              {/* UCAPAN */}
              <TabsContent value="ucapan" className="space-y-4">
                <SW label="Gunakan Ucapan (Buku Tamu)" checked={design.ucapan.enabled} onChange={(v) => setD("ucapan", "enabled", v)} testid="ucapan-enabled" hint="Menampilkan ucapan dari tamu undangan." />
                <SW label="Tampilkan Ucapan Secara Default" checked={design.ucapan.default_show} onChange={(v) => setD("ucapan", "default_show", v)} testid="ucapan-default-show" />
                <PhotoField label="Foto Section Ucapan" uploaded={design.ucapan.photo} url={design.ucapan.photo_url} uploading={uploading === "ucapan"}
                  onUpload={uploadDesign("ucapan", "photo", "ucapan")} onUrl={(v) => setD("ucapan", "photo_url", v)} onClear={() => setD("ucapan", "photo", "")} testid="ucapan-photo" />
              </TabsContent>

              {/* PENUTUP */}
              <TabsContent value="penutup" className="space-y-4">
                <SW label="Gunakan Bagian Penutup" checked={design.penutup.enabled} onChange={(v) => setD("penutup", "enabled", v)} testid="penutup-enabled" />
                <TF label="Judul" value={design.penutup.judul} onChange={(v) => setD("penutup", "judul", v)} testid="penutup-judul" />
                <TA label="Pesan Penutup" value={design.penutup.pesan} onChange={(v) => setD("penutup", "pesan", v)} testid="penutup-pesan" />
                <div className="grid gap-4 sm:grid-cols-2">
                  <TF label="Tanda Tangan Keluarga Pria" value={design.penutup.ttd_pria} onChange={(v) => setD("penutup", "ttd_pria", v)} testid="penutup-ttd-pria" />
                  <TF label="Tanda Tangan Keluarga Wanita" value={design.penutup.ttd_wanita} onChange={(v) => setD("penutup", "ttd_wanita", v)} testid="penutup-ttd-wanita" />
                </div>
                <PhotoField label="Foto Section Penutup" uploaded={design.penutup.photo} url={design.penutup.photo_url} uploading={uploading === "penutup"}
                  onUpload={uploadDesign("penutup", "photo", "penutup")} onUrl={(v) => setD("penutup", "photo_url", v)} onClear={() => setD("penutup", "photo", "")} testid="penutup-photo" />
              </TabsContent>
            </Tabs>
          </Card>
        </div>

        {/* Live preview */}
        <div className="mb-6 lg:sticky lg:top-6">
          <Card className="p-4">
            {pid ? (
              <InvitationPreview s={s} events={events} gallery={gallery} design={design} pasanganInfo={pasanganInfo} weddingDate={s.wedding_date || weddingDate} />
            ) : (
              <p className="py-16 text-center text-sm text-muted-foreground">Pilih pasangan di Home untuk melihat preview undangan.</p>
            )}
          </Card>
        </div>
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-md p-0" data-testid="preview-dialog">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle>Preview Undangan</DialogTitle>
            <DialogDescription>Tampilan undangan publik Anda.</DialogDescription>
          </DialogHeader>
          <div className="p-4">
            {link ? <iframe title="preview" src={link} className="h-[600px] w-full rounded-xl border border-border" /> : <p className="text-sm text-muted-foreground">Pilih pasangan dulu.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
